import { INVENTORY_ROUTES } from "@/lib/booking";
import type {
  Nationality,
  PassengerCategory,
  PassengerKey,
} from "@/types/trip";
import type { RouteTicketData } from "@/types/topup/routeticket";

export const TOPUP_KEYS: PassengerKey[] = [
  "Malaysian-Adult",
  "Malaysian-Child",
  "Foreigner-Adult",
  "Foreigner-Child",
];

export type TopupCounts = Record<string, number>;

export type TopupRouteEntry = {
  id: string;
  routeLabel: string;
  operatorCompanyRouteId?: string;
  counts: TopupCounts;
};

export const TOPUP_ROUTE_OPTIONS = INVENTORY_ROUTES.map((route) => ({
  value: `${route.origin} → ${route.destination}`,
  label: `${route.origin} → ${route.destination}`,
}));

export const topupRouteTitle = (label: string): string =>
  TOPUP_ROUTE_OPTIONS.find((option) => option.value === label)?.label ?? label;

export const EMPTY_TOPUP_COUNTS: TopupCounts = TOPUP_KEYS.reduce(
  (counts, key) => ({ ...counts, [key]: 0 }),
  {} as TopupCounts,
);

export const describeTopupTickets = (counts: TopupCounts): string => {
  const parts = TOPUP_KEYS.filter((key) => (counts[key] ?? 0) > 0).map(
    (key) => {
      const [nationality, category] = key.split("-") as [
        Nationality,
        PassengerCategory,
      ];
      return `${counts[key]} ${category} (${nationality === "Malaysian" ? "MYS" : "FOR"})`;
    },
  );

  return parts.length > 0 ? parts.join(", ") : "Select tickets";
};

export const countTopupTickets = (counts: TopupCounts): number =>
  Object.values(counts).reduce((total, qty) => total + (qty ?? 0), 0);

export const totalTopupTickets = (routes: TopupRouteEntry[]): number =>
  routes.reduce((total, route) => total + countTopupTickets(route.counts), 0);

export const validateTopupRoutePricing = (
  routes: TopupRouteEntry[],
  ticketTypesByRoute: Record<string, RouteTicketData[]> = {},
): string[] => {
  const issues: string[] = [];

  routes.forEach((route) => {
    if (!route.operatorCompanyRouteId) {
      issues.push(`Route "${route.routeLabel}" is missing its route ID.`);
      return;
    }

    const ticketTypes = ticketTypesByRoute[route.operatorCompanyRouteId] ?? [];

    if (ticketTypes.length === 0) {
      return;
    }

    ticketTypes.forEach((ticket) => {
      const qty = route.counts[ticket.mdTicketTypeId] ?? 0;

      if (qty <= 0) return;

      const price = Number(ticket.price);
      if (!Number.isFinite(price) || price < 0) {
        issues.push(
          `Route "${route.routeLabel}" ticket "${ticket.ticketTypeName}" has invalid pricing.`,
        );
      }
    });
  });

  return issues;
};

export const totalTopupAmount = (
  routes: TopupRouteEntry[],
  ticketTypesByRoute: Record<string, RouteTicketData[]> = {},
): number => {
  const pricingIssues = validateTopupRoutePricing(routes, ticketTypesByRoute);

  if (pricingIssues.length > 0) {
    return 0;
  }

  return routes.reduce((total, route) => {
    const ticketTypes = route.operatorCompanyRouteId
      ? ticketTypesByRoute[route.operatorCompanyRouteId]
      : undefined;

    if (!ticketTypes || ticketTypes.length === 0) {
      return total;
    }

    const routeAmount = ticketTypes.reduce(
      (sum, ticket) =>
        sum + (route.counts[ticket.mdTicketTypeId] ?? 0) * Number(ticket.price),
      0,
    );

    return total + routeAmount;
  }, 0);
};

export type TopupLine = {
  key: PassengerKey;
  label: string;
  nationality: Nationality;
  qty: number;
  amount: number;
};

export const buildTopupLines = (counts: TopupCounts): TopupLine[] =>
  TOPUP_KEYS.filter((key) => (counts[key] ?? 0) > 0).map((key) => {
    const [nationality, category] = key.split("-") as [
      Nationality,
      PassengerCategory,
    ];
    return {
      key,
      label: `${category} (${nationality})`,
      nationality,
      qty: counts[key] ?? 0,
      amount: 0,
    };
  });

export const serializeTopupRoutes = (routes: TopupRouteEntry[]): string =>
  routes
    .map(
      (route) =>
        `${route.routeLabel}|${TOPUP_KEYS.map((key) => `${key}:${route.counts[key] ?? 0}`).join(",")}`,
    )
    .join(";");

export const parseTopupRoutes = (
  value: string | undefined,
): TopupRouteEntry[] => {
  if (!value) return [];

  return value
    .split(";")
    .filter(Boolean)
    .map((chunk, index) => {
      const [routeLabel = "", countsPart = ""] = chunk.split("|");
      const counts = TOPUP_KEYS.reduce((acc, key) => {
        const match = countsPart
          .split(",")
          .find((part) => part.startsWith(`${key}:`));
        const parsed = Number(match?.split(":")[1]);
        return { ...acc, [key]: Number.isFinite(parsed) ? parsed : 0 };
      }, {} as TopupCounts);

      return { id: `${routeLabel}-${index}`, routeLabel, counts };
    });
};

export const validateTopupSearch = (
  search: Record<string, unknown>,
): { routes: string } => ({
  routes: typeof search["routes"] === "string" ? search["routes"] : "",
});